import { db, auth } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  serverTimestamp
} from 'firebase/firestore';
import { 
  signOut, 
  createUserWithEmailAndPassword, 
  updateProfile,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';

/**
 * Firebase Users service for user operations
 */
const UsersServices = {
  getItems: async () => {
    try {
      const usersQuery = collection(db, 'users');
      const querySnapshot = await getDocs(usersQuery);
      const users = [];
      
      querySnapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return users;
    } catch (e) {
      console.error('Get all users error:', e);
      return { error: e.message };
    }
  },
  
  searchItems: async (obj) => {
    try {
      let usersQuery = collection(db, 'users');
      
      // Convert search object to Firebase queries
      if (Object.keys(obj).length > 0) {
        // For simple implementation, we'll just search by the first key/value pair
        const [key, value] = Object.entries(obj)[0];
        usersQuery = query(usersQuery, where(key, '==', value));
      }
      
      const querySnapshot = await getDocs(usersQuery);
      const users = [];
      
      querySnapshot.forEach((doc) => {
        users.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return users;
    } catch (e) {
      console.error('Search users error:', e);
      return { error: e.message };
    }
  },
  
  getItem: async (obj) => {
    try {
      if (!obj.id) {
        throw new Error('No user ID provided');
      }
      
      const userRef = doc(db, 'users', obj.id);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return {
          id: userDoc.id,
          ...userDoc.data()
        };
      } else {
        return { error: 'User not found' };
      }
    } catch (e) {
      console.error('Get user error:', e);
      return { error: e.message };
    }
  },
  
  loginItem: async (obj) => {
    // Note: Actual Firebase login is typically handled by AuthServices
    // This is just to match the structure of PocketBase version
    try {
      if (!obj.email || !obj.password) {
        throw new Error('Email and password are required');
      }
      
      // Use signInWithEmailAndPassword from firebase/auth
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const userCredential = await signInWithEmailAndPassword(auth, obj.email, obj.password);
      const user = userCredential.user;
      
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return {
          id: userDoc.id,
          ...userDoc.data()
        };
      } else {
        // User exists in Auth but not in Firestore, create a basic profile
        const userData = {
          email: user.email,
          name: user.displayName || '',
          uid: user.uid,
          created: serverTimestamp(),
          updated: serverTimestamp()
        };
        
        await setDoc(userRef, userData);
        
        return {
          id: user.uid,
          ...userData
        };
      }
    } catch (e) {
      console.error('Login user error:', e);
      return { error: e.message };
    }
  },
  
  addItem: async (obj) => {
    try {
      // First create the user in Firebase Authentication
      if (!obj.email || !obj.password) {
        throw new Error('Email and password are required');
      }

      // Validate password length
      if (obj.password.length < 6) {
        return { error: 'Password should be at least 6 characters' };
      }

      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        obj.email, 
        obj.password
      );
      
      const user = userCredential.user;
      
      // If name is provided, update the profile
      if (obj.name) {
        await updateProfile(user, {
          displayName: obj.name
        });
      }
      
      // Now create user profile document in Firestore
      const userRef = doc(db, 'users', user.uid);
      
      // Prepare user data without password
      const { password, passwordConfirm, ...userData } = obj;
      
      const userDataToSave = {
        ...userData,
        uid: user.uid,
        email: user.email,
        created: serverTimestamp(),
        updated: serverTimestamp()
      };
      
      await setDoc(userRef, userDataToSave);
      
      // Get the created document
      const userDoc = await getDoc(userRef);
      
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (e) {
      console.error('Add user error:', e);
      return { error: e.message };
    }
  },
  
  updateItem: async (id, obj) => {
    try {
      const userRef = doc(db, 'users', id);
      
      // Check if user exists
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = {
        ...obj,
        updated: serverTimestamp()
      };
      
      await updateDoc(userRef, userData);
      
      // Get the updated document
      const updatedDoc = await getDoc(userRef);
      
      return {
        id: updatedDoc.id,
        ...updatedDoc.data()
      };
    } catch (e) {
      console.error('Update user error:', e);
      return { error: e.message };
    }
  },
  
  deleteItem: async (obj) => {
    try {
      if (!obj.id) {
        throw new Error('No user ID provided');
      }
      
      const userRef = doc(db, 'users', obj.id);
      await deleteDoc(userRef);
      
      return { success: true };
    } catch (e) {
      console.error('Delete user error:', e);
      return { success: false, error: e.message };
    }
  },
  
  logOut: async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (e) {
      console.error('Logout error:', e);
      return { success: false, error: e.message };
    }
  },
  
  loginWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;
      
      // Check if user exists in Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        return {
          id: userDoc.id,
          ...userDoc.data()
        };
      } else {
        // User authenticated with Google but not in Firestore, create a profile
        const userData = {
          email: user.email,
          name: user.displayName || '',
          photoURL: user.photoURL || '',
          uid: user.uid,
          role: 3, // Default role for new users
          created: serverTimestamp(),
          updated: serverTimestamp()
        };
        
        await setDoc(userRef, userData);
        
        return {
          id: user.uid,
          ...userData
        };
      }
    } catch (e) {
      console.error('Google login error:', e);
      return { error: e.message };
    }
  }
};

export default UsersServices; 